import { RESOLUTIONS, type Resolution } from '../capacity-plan/types';

/**
 * The one timeframe vocabulary the planners offer (ENG-918).
 *
 * The Production Plan and the Project Plan both scope their rows to a period,
 * and both did it with their own list: the planner offered Day…Year plus
 * `anytime`, the project offered `everything` plus Year…Day — no Quarter, the
 * opposite ordering and a different word for the same idea. Two vocabularies
 * for one concept is a bug in itself, and it also made a saved view's window
 * unportable between them. This is now the single source of truth; each
 * surface keeps only its own URL-param NAME (`resolution` on the planner,
 * `time_window` on the project — both established deep-link contracts).
 *
 * Ordered narrowest-first with the unbounded option last, so the select reads
 * as a zoom control rather than an arbitrary list.
 */
export const TIME_WINDOWS = [...RESOLUTIONS, 'anytime'] as const;

export type TimeWindow = (typeof TIME_WINDOWS)[number];

/**
 * A window with real boundaries. Identical to the Capacity Plan's `Resolution`
 * by design — Anytime is a planner/project-level "ignore the window" mode that
 * the period maths never sees.
 */
export type BoundedTimeWindow = Resolution;

/** True when the window has a period to compute, label and step through. */
export function isBoundedTimeWindow(value: TimeWindow): value is BoundedTimeWindow {
  return value !== 'anytime';
}

/**
 * Narrow a time window for the period maths. Anytime borrows Week so that
 * stepping away from it lands somewhere sensible; callers that must not window
 * at all check {@link isBoundedTimeWindow} first.
 */
export function toBoundedTimeWindow(value: TimeWindow): BoundedTimeWindow {
  return value === 'anytime' ? 'week' : value;
}

/**
 * Label keys in the shared `grid` namespace, so both planners name the same
 * window with the same word. Kept as a lookup (rather than `timeWindow.${v}`)
 * so a new member of the union is a compile error until it has copy.
 */
export const TIME_WINDOW_LABEL_KEYS = {
  day: 'timeWindow.day',
  week: 'timeWindow.week',
  month: 'timeWindow.month',
  quarter: 'timeWindow.quarter',
  year: 'timeWindow.year',
  anytime: 'timeWindow.anytime',
} as const satisfies Record<TimeWindow, string>;
